import { Component, Input, OnDestroy, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, DoCheck, Injector, ViewChild } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { WFSectionLocalResource, WFSectionLocalStatus, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { Configuration } from '../../../checklist-builder/checklist-builder-sections/checklist-builder-section/checklist-builder-section.component';
import { Checklist, ChecklistTemplate } from '../../../checklists';
import { ChecklistsService } from '../../../checklists.service';
import { ChecklistStepsComponent } from '../../checklist-steps/checklist-steps.component';
import { ComponentType, FormStatusEnum } from 'src/app/common/enumerations/enumerations';

@Component({
  selector: "checklist-section",
  templateUrl: "./checklist-section.component.html",
  styleUrls: ["./checklist-section.component.scss"],
})
export class ChecklistSectionComponent extends BaseComponent implements OnDestroy, OnInit, OnChanges, DoCheck {
  @Input() currentChecklist?: Checklist | null;
  @Input() currentChecklistTemplate?: ChecklistTemplate;
  @Input() section?: WFSectionLocalResource;
  @Input() displayNumber?: number;
  @Input() precheck!: boolean;
  @Input() disabled?: boolean;

  @Output() checkShared = new EventEmitter<any>();
  @Output() taskChanged = new EventEmitter<WfTaskLocalResource>();
  @Output() viewportChanged = new EventEmitter<void>();

  checklists!: Checklist[];
  checklistsFiltered!: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;

  roles!: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  config!: Configuration;
  userCanDuplicate!: boolean;
  hasCompletedTasks!: boolean;

  private _sectionId: number | undefined;
  override isExpanded: boolean = false;
  isExpanding: boolean = false;
  private _lastCollapsed: boolean | null | undefined;
  private expandRenderTimeout?: ReturnType<typeof setTimeout>;

  tooltip!: string;
  tooltipd!: string;

  wfSectionLocalStatus = WFSectionLocalStatus;
  checklistClosed?: boolean;

  @ViewChild(ChecklistStepsComponent) stepsComponent!: ChecklistStepsComponent;

  private readonly genericKeyEnableDocumentTypeID = 300;
  private readonly genericKeyEnableTemplateDocumentTypeID = 301;

  constructor(
    protected override injector: Injector,
    private service: ChecklistsService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentChecklist']) {
      this.section = this.currentChecklist?.wfTable?.wfTableLocal?.wfSectionLocals.find(x => x.id == this.section?.id);
    }
    if (this.section) {
      // Only re-initialize expansion state when the section identity changes (not on every store refresh).
      // This preserves the user's manually toggled state across store updates.
      if (this.section.id !== this._sectionId) {
        this._sectionId = this.section.id;
        this._lastCollapsed = this.section.collapsed;
        this.isExpanded = this.isSectionExpanded();
      }
      this.config = this.utils.JSONparse(this.section.configuration);
      if (!this.config) { this.config = new Configuration(); }
      this.loadRoles();
      this.userCanDuplicate = this.hasRoles(this.config?.roleIDs);
      this.checklistClosed = this.currentChecklist?.checklistStatus?.formStatus.closedStatus || this.currentChecklist?.checklistStatus?.formStatus.cancelStatus;

      const stepsWithoutName = this.section.wfStepLocals?.filter(s => !s.name && s.collapsible) ?? [];
      if (stepsWithoutName.length) {
        stepsWithoutName.forEach(s => {
          s.name = this.section?.name ?? 'Step ' + s.number;
        });
      }

    }
  }

  ngDoCheck(): void {
    // Detect when section.collapsed is changed externally (e.g., Expand All / Collapse All button),
    // which doesn't trigger ngOnChanges because it's a property mutation, not a new object reference.
    if (this.section && this._sectionId === this.section.id && this.section.collapsed !== this._lastCollapsed) {
      this._lastCollapsed = this.section.collapsed;
      this.clearExpandRenderTimeout();
      this.isExpanding = false;
      this.isExpanded = this.isSectionExpanded();
      this.viewportChanged.emit();
    }
  }

  ngOnInit(): void {

  }

  /**
   * Returns the user who claimed a Generic KE section containing the Shieldings KE component.
   * Signed tasks and persisted shielding item users are both considered.
   */
  get sectionOwnerUserID(): number | undefined {
    if (!this.isGenericKeyEnableShieldingSection())
      return undefined;

    const tasks = [...(this.section?.wfStepLocals ?? [])]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .flatMap(step => [...(step.wfTaskLocals ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

    for (const task of tasks) {
      const signedUserID = task.wfTasks?.find(wfTask => wfTask.signedBy?.id != null)?.signedBy?.id;
      if (signedUserID != null)
        return signedUserID;

      if (task.componentID === ComponentType.ShieldingsKE) {
        const shieldingUserID = this.getShieldingUserID(task.text);
        if (shieldingUserID != null)
          return shieldingUserID;
      }
    }

    return undefined;
  }

  /**
   * Applies the existing checklist disabled state and the Generic KE section ownership rule.
   */
  get sectionDisabled(): boolean {
    const ownerUserID = this.sectionOwnerUserID;
    return !!this.disabled || (ownerUserID != null && ownerUserID !== this.getCurrentUser()?.id);
  }

  /**
   * Indicates whether the section contains a step with at least one currently visible task.
   */
  get hasVisibleSteps(): boolean {
    return this.section?.wfStepLocals?.some(step => {
      const hidden = step.wfStepStatusLocals
        ?.find(status => status.status == this.currentChecklist?.checklistStatusID)?.hidden ?? false;
      const statusDisabled = !step.wfStepStatusLocals
        .map(status => status.status).includes(this.currentChecklist?.checklistStatusID);
      // Apply the same Hide/Disable rule used by checklist-task to prevent empty steps.
      const hasVisibleTasks = step.wfTaskLocals?.some(task => {
        const disableInsteadOfHide = !!this.utils.JSONparse(task.configuration)?.hideDisable;
        return task.required !== false &&
          (!(statusDisabled || this.sectionDisabled) || !task.condition || disableInsteadOfHide);
      }) ?? false;
      return !hidden && hasVisibleTasks;
    }) ?? false;
  }

  /**
   * Identifies the Generic KE checklist section that owns a Shieldings KE task.
   */
  private isGenericKeyEnableShieldingSection(): boolean {
    const isGenericKeyEnable =
      this.currentChecklist?.documentTypeID === this.genericKeyEnableDocumentTypeID ||
      this.currentChecklist?.checklistTemplate?.documentTypeID === this.genericKeyEnableTemplateDocumentTypeID ||
      this.currentChecklistTemplate?.documentTypeID === this.genericKeyEnableTemplateDocumentTypeID;
    // Match the old KE execution rule: ownership is enforced while the checklist is in Shielding.
    const isShieldingStatus =
      this.currentChecklist?.status === FormStatusEnum.Shielding ||
      this.currentChecklist?.checklistStatus?.formStatusID === FormStatusEnum.Shielding;
    const hasShieldingsKe = this.section?.wfStepLocals
      ?.some(step => step.wfTaskLocals?.some(task => task.componentID === ComponentType.ShieldingsKE));

    return isGenericKeyEnable && isShieldingStatus && !!hasShieldingsKe;
  }

  /**
   * Reads the first checked shielding user's ID from the component's persisted task value.
   * @param text Serialized Shieldings KE task value.
   */
  private getShieldingUserID(text?: string | null): number | undefined {
    const values = this.utils.JSONparse(this.utils.getTextValue(text));
    if (!Array.isArray(values))
      return undefined;

    return values.find(value => value?.checked && value?.user?.id != null)?.user?.id;
  }

  loadRoles() {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      if (data.length) {
        this.roles = data.filter(r => this.config.roleIDs.includes(r.id));
        this.tooltip = 'Duplicate Section (Allowed roles: ' + this.roles?.map(r => r.code).join(', ') + ')';
        this.tooltipd = 'Delete Duplicated Section (Allowed roles: ' + this.roles?.map(r => r.code).join(', ') + ')';
      }
    });
  }

  override ngOnDestroy(): void {
    this.clearExpandRenderTimeout();
    this.checklistsSubs?.unsubscribe();
    this.rolesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  isSectionExpanded(): boolean {
    const expanded = this.section?.collapsible ? !this.section?.collapsed : true;
    return expanded;
  }

  /**
   * Saves section collapsed state and delays heavy child rendering long enough to show feedback.
   * @param collapsed New collapsed state from the expansion panel.
   */
  saveState(collapsed: boolean) {
    if (this.section && this.currentChecklist) {
      this.setSectionRenderState(collapsed);
      this._lastCollapsed = collapsed;
      this.viewportChanged.emit();

      if (this.section.wfStepLocals?.length == 1 && !collapsed && this.section.wfStepLocals[0].collapsible) {
        this.section.wfStepLocals[0].collapsed = collapsed;
        this.service.saveStepExpanded(this.currentChecklist!.id, this.section.wfStepLocals[0].id, collapsed);
      }

      if (collapsed) {
        this.section.wfStepLocals?.map((step) => {
          if (step.collapsible) {
            step.collapsed = collapsed;
            this.service.saveStepExpanded(this.currentChecklist!.id, step.id, collapsed);
          }
        });
      }

      if (collapsed !== this.section.collapsed) {
        this.section.collapsed = collapsed;
        this.service.saveSectionExpanded(this.currentChecklist.id, this.section.id, collapsed);
      }
    }
  }

  /**
   * Shows an immediate loading indicator before creating the nested checklist step tree.
   * @param collapsed New collapsed state from the expansion panel.
   */
  private setSectionRenderState(collapsed: boolean): void {
    this.clearExpandRenderTimeout();
    if (collapsed) {
      this.isExpanding = false;
      this.isExpanded = false;
      return;
    }

    // Paint a small loading cue before instantiating the heavy checklist steps component tree.
    this.isExpanding = true;
    this.isExpanded = false;
    this.expandRenderTimeout = setTimeout(() => {
      this.isExpanded = true;
      this.isExpanding = false;
      this.viewportChanged.emit();
    }, 50);
  }

  /**
   * Clears a pending section render delay.
   */
  private clearExpandRenderTimeout(): void {
    if (this.expandRenderTimeout) {
      clearTimeout(this.expandRenderTimeout);
      this.expandRenderTimeout = undefined;
    }
  }

  duplicate(e: any) {
    e.stopPropagation();
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: "400px",
      data: {
        message: this.getMessage("ChecklistDuplicateSectionConfirmation")
          .description,
        icon: "question",
      },
    });
    yesno.afterClosed().subscribe(async (data) => {
      if (data && this.section) {
        await this.service.duplicateSection(this.section.id).toPromise();
      }
    });
  }

  delete(e: any) {
    e.stopPropagation();
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: "400px",
      data: {
        message: this.getMessage("ChecklistDeleteSectionConfirmation")
          .description,
        icon: "question",
      },
    });
    yesno.afterClosed().subscribe(async (data) => {
      if (data && this.section) {
        await this.service.deleteSection(this.section.id).toPromise();
      }
    });
  }

}
